import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';

interface Appointment {
  id: string;
  applicant_name: string;
  email: string;
  phone: string;
  appointment_date: string;
  is_lagos: boolean;
  attendance_status: 'pending' | 'attended' | 'no_show';
}

type RescheduleType = 'zoom' | 'in-person';

interface RescheduleForm {
  appointmentId: string;
  rescheduleType: RescheduleType;
  newDate: string;
  newTime: string;
  customNote: string;
}

const ZOOM_LINK = 'https://us05web.zoom.us/j/7843348765?pwd=gZgQ5j4kJa7pOhFhzBwdGby1f3NdlX.1&omn=85899546011';
const ZOOM_ID = '784 334 8764';
const ZOOM_PASSWORD = 'MMP';
const PHYSICAL_ADDRESS = '#2 Allen Avenue, Gabriel Akinmade Taylor Plaza, Near Toyin Roundabout, Ikeja. 3rd Floor, Winning Life Center';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

export default function RescheduleTab() {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'no_show' | 'pending'>('all');
  const [rescheduleModal, setRescheduleModal] = useState<Appointment | null>(null);
  const [form, setForm] = useState<RescheduleForm>({
    appointmentId: '',
    rescheduleType: 'zoom',
    newDate: '',
    newTime: '21:00',
    customNote: '',
  });
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ success: boolean; message: string } | null>(null);

  // Bulk reschedule
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkForm, setBulkForm] = useState<{
    rescheduleType: RescheduleType;
    newDate: string;
    newTime: string;
    customNote: string;
  }>({
    rescheduleType: 'zoom',
    newDate: '',
    newTime: '21:00',
    customNote: '',
  });
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState<{ success: number; failed: number } | null>(null);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('appointments')
      .select('id, applicant_name, email, phone, appointment_date, is_lagos, attendance_status')
      .order('appointment_date', { ascending: false });

    if (!error) {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const past = ((data || []) as Appointment[]).filter(
        app => new Date(app.appointment_date) < now
      );
      setAppointments(past);
    }
    setLoading(false);
  };

  const filtered = appointments.filter(app => {
    const matchSearch =
      app.applicant_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus =
      filterStatus === 'all' ||
      (filterStatus === 'no_show' && app.attendance_status === 'no_show') ||
      (filterStatus === 'pending' && (app.attendance_status === 'pending' || !app.attendance_status));
    return matchSearch && matchStatus;
  });

  const openReschedule = (app: Appointment) => {
    setRescheduleModal(app);
    setSendResult(null);
    setForm({
      appointmentId: app.id,
      rescheduleType: 'zoom',
      newDate: '',
      newTime: '21:00',
      customNote: '',
    });
  };

  const closeModal = () => {
    setRescheduleModal(null);
    setSendResult(null);
  };

  const handleSendReschedule = async () => {
    if (!form.newDate || !form.newTime) {
      setSendResult({ success: false, message: 'Please select a new date and time.' });
      return;
    }
    setSending(true);
    setSendResult(null);

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/schedule-reminder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'send_reschedule',
          appointmentId: form.appointmentId,
          rescheduleType: form.rescheduleType,
          newDate: form.newDate,
          newTime: form.newTime,
          customNote: form.customNote.trim() || undefined,
        }),
      });

      const result = await res.json();
      if (res.ok && result.success) {
        setSendResult({ success: true, message: `Reschedule sent to ${rescheduleModal?.applicant_name}!` });
      } else {
        setSendResult({ success: false, message: result.error || 'Failed to send reschedule.' });
      }
    } catch {
      setSendResult({ success: false, message: 'Network error. Please try again.' });
    }

    setSending(false);
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length && filtered.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(a => a.id)));
    }
  };

  const handleBulkReschedule = async () => {
    if (!bulkForm.newDate || !bulkForm.newTime) {
      return;
    }
    setBulkSending(true);
    setBulkResult(null);

    const targets = filtered.filter(a => selectedIds.has(a.id));
    let success = 0;
    let failed = 0;

    for (const app of targets) {
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/schedule-reminder`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: 'send_reschedule',
            appointmentId: app.id,
            rescheduleType: bulkForm.rescheduleType,
            newDate: bulkForm.newDate,
            newTime: bulkForm.newTime,
            customNote: bulkForm.customNote.trim() || undefined,
          }),
        });
        const result = await res.json();
        if (res.ok && result.success) success++;
        else failed++;
      } catch {
        failed++;
      }
    }

    setBulkResult({ success, failed });
    setBulkSending(false);
    setSelectedIds(new Set());
    setBulkModal(false);
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-NG', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
      timeZone: 'Africa/Lagos',
    });

  const getStatusBadge = (status: string) => {
    if (status === 'no_show') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700"><i className="ri-close-circle-line text-xs"></i> No-Show</span>;
    if (status === 'attended') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700"><i className="ri-checkbox-circle-line text-xs"></i> Attended</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-stone-100 text-stone-500"><i className="ri-time-line text-xs"></i> Pending</span>;
  };

  const allSelected = filtered.length > 0 && selectedIds.size === filtered.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <i className="ri-calendar-line text-amber-600 text-xl"></i>
        </div>
        <div>
          <h3 className="font-semibold text-amber-900 mb-1">Manual Reschedule</h3>
          <p className="text-sm text-amber-700">
            Send a reschedule notification to any applicant — to <strong>Zoom</strong> or <strong>In-Person</strong>, regardless of their original appointment type. Select multiple applicants for bulk reschedule.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <div className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <i className="ri-search-line text-stone-400 text-sm"></i>
          </div>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
          />
        </div>
        <div className="flex items-center bg-stone-100 rounded-lg p-1 gap-1 flex-shrink-0">
          {(['all', 'no_show', 'pending'] as const).map(s => (
            <button
              key={s}
              onClick={() => { setFilterStatus(s); setSelectedIds(new Set()); }}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
                filterStatus === s ? 'bg-white text-amber-700 shadow-sm' : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              {s === 'all' ? 'All' : s === 'no_show' ? 'No-Show' : 'Pending'}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-stone-800 text-white rounded-xl px-5 py-3 flex items-center justify-between gap-4 shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
              <i className="ri-checkbox-multiple-line text-white text-base"></i>
            </div>
            <div>
              <p className="font-semibold text-sm">{selectedIds.size} selected</p>
              <p className="text-xs text-stone-300">Bulk reschedule all selected applicants</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setBulkModal(true); setBulkResult(null); }}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white rounded-lg text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className="ri-calendar-line text-sm"></i>
              Reschedule All {selectedIds.size}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-stone-300 hover:text-white text-sm cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Bulk Result */}
      {bulkResult && (
        <div className={`rounded-lg px-4 py-3 flex items-center gap-3 ${bulkResult.failed === 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className={`text-sm ${bulkResult.failed === 0 ? 'ri-checkbox-circle-line text-emerald-600' : 'ri-alert-line text-amber-600'}`}></i>
          </div>
          <p className={`text-sm font-medium ${bulkResult.failed === 0 ? 'text-emerald-800' : 'text-amber-800'}`}>
            ✅ {bulkResult.success} reschedule{bulkResult.success !== 1 ? 's' : ''} sent
            {bulkResult.failed > 0 && ` · ❌ ${bulkResult.failed} failed`}
          </p>
          <button onClick={() => setBulkResult(null)} className="ml-auto text-stone-400 hover:text-stone-600 cursor-pointer">
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
      )}

      {/* Select All row */}
      {filtered.length > 0 && (
        <div className="flex items-center justify-between px-1">
          <p className="text-sm text-stone-500">{filtered.length} applicant{filtered.length !== 1 ? 's' : ''}</p>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allSelected}
              ref={el => { if (el) el.indeterminate = someSelected; }}
              onChange={toggleSelectAll}
              className="w-4 h-4 text-amber-500 border-stone-300 rounded focus:ring-amber-500 cursor-pointer"
            />
            <span className="text-sm font-medium text-stone-700 whitespace-nowrap">
              {allSelected ? 'Deselect All' : 'Select All'}
            </span>
          </label>
        </div>
      )}

      {/* Table — Desktop */}
      <div className="hidden lg:block overflow-x-auto bg-white rounded-lg border border-stone-200">
        <table className="w-full">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 text-amber-500 border-stone-300 rounded focus:ring-amber-500 cursor-pointer"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Email</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Phone</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Original Appt</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Attendance</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-200">
            {filtered.map(app => (
              <tr
                key={app.id}
                className={`hover:bg-stone-50 transition-colors ${selectedIds.has(app.id) ? 'bg-amber-50/40' : ''}`}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(app.id)}
                    onChange={() => toggleSelect(app.id)}
                    className="w-4 h-4 text-amber-500 border-stone-300 rounded focus:ring-amber-500 cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3 text-sm font-medium text-stone-900">{app.applicant_name}</td>
                <td className="px-4 py-3 text-sm text-stone-600">{app.email}</td>
                <td className="px-4 py-3 text-sm">
                  <a
                    href={`https://wa.me/${app.phone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-600 hover:underline flex items-center gap-1"
                  >
                    <i className="ri-whatsapp-line text-xs"></i>
                    {app.phone}
                  </a>
                </td>
                <td className="px-4 py-3 text-sm text-stone-700">{formatDate(app.appointment_date)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    app.is_lagos ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-800'
                  }`}>
                    {app.is_lagos ? <><i className="ri-map-pin-line text-xs"></i> In-Person</> : <><i className="ri-video-line text-xs"></i> Zoom</>}
                  </span>
                </td>
                <td className="px-4 py-3">{getStatusBadge(app.attendance_status || 'pending')}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => openReschedule(app)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors"
                  >
                    <i className="ri-calendar-line text-xs"></i>
                    Reschedule
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-12 text-stone-400">
            <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
              <i className="ri-calendar-line text-4xl"></i>
            </div>
            <p className="font-medium text-stone-500">No applicants found.</p>
          </div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-3">
        {filtered.map(app => (
          <div
            key={app.id}
            className={`bg-white rounded-lg border border-stone-200 p-4 space-y-3 ${selectedIds.has(app.id) ? 'ring-2 ring-amber-400' : ''}`}
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={selectedIds.has(app.id)}
                onChange={() => toggleSelect(app.id)}
                className="w-4 h-4 text-amber-500 border-stone-300 rounded focus:ring-amber-500 cursor-pointer mt-0.5"
              />
              <div className="flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold text-stone-900 text-sm">{app.applicant_name}</p>
                    <p className="text-xs text-stone-500">{app.email}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                    app.is_lagos ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-800'
                  }`}>
                    {app.is_lagos ? 'In-Person' : 'Zoom'}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <span className="text-xs text-stone-500">{formatDate(app.appointment_date)}</span>
                  {getStatusBadge(app.attendance_status || 'pending')}
                </div>
              </div>
            </div>
            <button
              onClick={() => openReschedule(app)}
              className="w-full flex items-center justify-center gap-2 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold cursor-pointer transition-colors"
            >
              <i className="ri-calendar-line text-sm"></i>
              Reschedule
            </button>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-stone-400 bg-white rounded-lg border border-stone-200">
            <p className="font-medium text-stone-500">No applicants found.</p>
          </div>
        )}
      </div>

      {/* ── Single Reschedule Modal ── */}
      {rescheduleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-line text-amber-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="font-bold text-stone-900 text-base">Send Reschedule</h3>
                  <p className="text-xs text-stone-500">{rescheduleModal.applicant_name}</p>
                </div>
              </div>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer rounded-lg hover:bg-stone-100">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Reschedule Type */}
            <div>
              <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">
                Reschedule To
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setForm(f => ({ ...f, rescheduleType: 'zoom', newTime: '21:00' }))}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-semibold cursor-pointer transition-all ${
                    form.rescheduleType === 'zoom'
                      ? 'border-stone-800 bg-stone-800 text-white'
                      : 'border-stone-200 bg-white text-stone-600 hover:border-stone-400'
                  }`}
                >
                  <i className="ri-video-line text-base"></i>
                  Zoom Meeting
                </button>
                <button
                  onClick={() => setForm(f => ({ ...f, rescheduleType: 'in-person', newTime: '11:00' }))}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-semibold cursor-pointer transition-all ${
                    form.rescheduleType === 'in-person'
                      ? 'border-emerald-700 bg-emerald-700 text-white'
                      : 'border-stone-200 bg-white text-stone-600 hover:border-stone-400'
                  }`}
                >
                  <i className="ri-map-pin-line text-base"></i>
                  In-Person
                </button>
              </div>
            </div>

            {/* Venue Info */}
            <div className={`rounded-xl p-4 text-sm ${form.rescheduleType === 'zoom' ? 'bg-stone-800 text-gray-300' : 'bg-emerald-50 border border-emerald-200 text-emerald-800'}`}>
              {form.rescheduleType === 'zoom' ? (
                <div className="space-y-1">
                  <p className="text-amber-400 font-semibold text-xs uppercase tracking-wider mb-2">Zoom Details</p>
                  <p><span className="text-stone-400">Link:</span> <span className="text-amber-400 break-all">{ZOOM_LINK}</span></p>
                  <p><span className="text-stone-400">ID:</span> {ZOOM_ID}</p>
                  <p><span className="text-stone-400">Password:</span> {ZOOM_PASSWORD}</p>
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="text-emerald-700 font-semibold text-xs uppercase tracking-wider mb-2">Physical Venue</p>
                  <p>{PHYSICAL_ADDRESS}</p>
                </div>
              )}
            </div>

            {/* New Date */}
            <div>
              <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">
                New Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form.newDate}
                onChange={e => setForm(f => ({ ...f, newDate: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
              />
            </div>

            {/* New Time */}
            <div>
              <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">
                New Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={form.newTime}
                onChange={e => setForm(f => ({ ...f, newTime: e.target.value }))}
                className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
              />
              <p className="text-xs text-stone-400 mt-1">
                {form.rescheduleType === 'zoom' ? 'Default: 9:00 PM (WAT)' : 'Default: 11:00 AM'}
              </p>
            </div>

            {/* Custom Note */}
            <div>
              <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">
                Additional Note (optional)
              </label>
              <textarea
                value={form.customNote}
                onChange={e => { if (e.target.value.length <= 500) setForm(f => ({ ...f, customNote: e.target.value })); }}
                placeholder="Add a personal note to include with the reschedule notification..."
                rows={3}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm resize-none"
              />
              <p className="text-xs text-stone-400 text-right mt-1">{form.customNote.length}/500</p>
            </div>

            {/* Result */}
            {sendResult && (
              <div className={`rounded-lg px-4 py-3 flex items-center gap-2 ${sendResult.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <i className={`text-sm ${sendResult.success ? 'ri-checkbox-circle-line text-emerald-600' : 'ri-error-warning-line text-red-600'}`}></i>
                <p className={`text-sm font-medium ${sendResult.success ? 'text-emerald-800' : 'text-red-800'}`}>{sendResult.message}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              <button
                onClick={closeModal}
                disabled={sending}
                className="flex-1 px-4 py-2.5 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendReschedule}
                disabled={sending || !form.newDate || !form.newTime}
                className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors text-sm font-semibold whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Sending...</>
                ) : (
                  <><i className="ri-send-plane-fill text-sm"></i> Send Reschedule</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bulk Reschedule Modal ── */}
      {bulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-line text-amber-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="font-bold text-stone-900 text-base">Bulk Reschedule</h3>
                  <p className="text-xs text-stone-500">{selectedIds.size} applicants selected</p>
                </div>
              </div>
              <button onClick={() => setBulkModal(false)} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer rounded-lg hover:bg-stone-100">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Reschedule Type */}
            <div>
              <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">Reschedule To</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setBulkForm(f => ({ ...f, rescheduleType: 'zoom', newTime: '21:00' }))}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-semibold cursor-pointer transition-all ${
                    bulkForm.rescheduleType === 'zoom'
                      ? 'border-stone-800 bg-stone-800 text-white'
                      : 'border-stone-200 bg-white text-stone-600 hover:border-stone-400'
                  }`}
                >
                  <i className="ri-video-line text-base"></i> Zoom Meeting
                </button>
                <button
                  onClick={() => setBulkForm(f => ({ ...f, rescheduleType: 'in-person', newTime: '11:00' }))}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-semibold cursor-pointer transition-all ${
                    bulkForm.rescheduleType === 'in-person'
                      ? 'border-emerald-700 bg-emerald-700 text-white'
                      : 'border-stone-200 bg-white text-stone-600 hover:border-stone-400'
                  }`}
                >
                  <i className="ri-map-pin-line text-base"></i> In-Person
                </button>
              </div>
            </div>

            {/* Venue Info */}
            <div className={`rounded-xl p-4 text-sm ${bulkForm.rescheduleType === 'zoom' ? 'bg-stone-800 text-gray-300' : 'bg-emerald-50 border border-emerald-200 text-emerald-800'}`}>
              {bulkForm.rescheduleType === 'zoom' ? (
                <div className="space-y-1">
                  <p className="text-amber-400 font-semibold text-xs uppercase tracking-wider mb-2">Zoom Details</p>
                  <p><span className="text-stone-400">Link:</span> <span className="text-amber-400 break-all">{ZOOM_LINK}</span></p>
                  <p><span className="text-stone-400">ID:</span> {ZOOM_ID} &nbsp; <span className="text-stone-400">Password:</span> {ZOOM_PASSWORD}</p>
                </div>
              ) : (
                <div>
                  <p className="text-emerald-700 font-semibold text-xs uppercase tracking-wider mb-2">Physical Venue</p>
                  <p>{PHYSICAL_ADDRESS}</p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">New Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={bulkForm.newDate}
                  onChange={e => setBulkForm(f => ({ ...f, newDate: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2.5 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">New Time <span className="text-red-500">*</span></label>
                <input
                  type="time"
                  value={bulkForm.newTime}
                  onChange={e => setBulkForm(f => ({ ...f, newTime: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">Additional Note (optional)</label>
              <textarea
                value={bulkForm.customNote}
                onChange={e => { if (e.target.value.length <= 500) setBulkForm(f => ({ ...f, customNote: e.target.value })); }}
                placeholder="Add a note to include with all reschedule notifications..."
                rows={3}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm resize-none"
              />
              <p className="text-xs text-stone-400 text-right mt-1">{bulkForm.customNote.length}/500</p>
            </div>

            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setBulkModal(false)}
                disabled={bulkSending}
                className="flex-1 px-4 py-2.5 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkReschedule}
                disabled={bulkSending || !bulkForm.newDate || !bulkForm.newTime}
                className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors text-sm font-semibold whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkSending ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Sending...</>
                ) : (
                  <><i className="ri-send-plane-fill text-sm"></i> Send to All {selectedIds.size}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
